import { useCallback, useEffect, useRef, useState } from "react";
import { projectsApi } from "../api/projectsApi";
import { monitoringApi } from "../../monitoring/api/monitoringApi";
import { CUSTOMER_STATUSES } from "../constants/projectsViewV2Constants";
import { projStatusPill } from "../services/projectsViewDerivations";
import UserSearchSelect from "../../common/components/UserSearchSelect";
import DrawerTabs from "../../common/components/DrawerTabs";
import "../../../V2SideDrawer.css";

export default function CustomerDrawer({ item, projects, users, isAdmin, onClose, onSaved, showToast }) {
  const isEdit = !!item;
  const [tab,   setTab]   = useState("details");
  const [form,  setForm]  = useState({
    customer_name:   item?.customer_name   || "",
    customer_code:   item?.customer_code   || "",
    status:          item?.status          || "Active",
    account_manager: item?.account_manager || "",
  });
  const [saving, setSaving] = useState(false);

  // ── Products tab state ──────────────────────────────────────────────────────
  const [allProducts,      setAllProducts]      = useState([]);
  const [linkedProducts,   setLinkedProducts]   = useState([]);
  const [productsLoading,  setProductsLoading]  = useState(false);
  const [selectedProduct,  setSelectedProduct]  = useState("");
  const [linking,          setLinking]          = useState(false);
  const [removingId,       setRemovingId]       = useState(null);

  const loadProductsTab = useCallback(async () => {
    if (!isEdit) return;
    setProductsLoading(true);
    try {
      const [all, linked] = await Promise.all([
        projectsApi.listProducts(),
        projectsApi.listCustomerProducts(item.id),
      ]);
      setAllProducts(Array.isArray(all)    ? all    : []);
      setLinkedProducts(Array.isArray(linked) ? linked : []);
    } catch {
      showToast?.("Failed to load products", "warn");
    } finally {
      setProductsLoading(false);
    }
  }, [isEdit, item?.id, showToast]);

  useEffect(() => {
    if (tab === "products") loadProductsTab();
  }, [tab, loadProductsTab]);

  const linkedIds = new Set(linkedProducts.map(p => p.id));
  const availableProducts = allProducts.filter(p => !linkedIds.has(p.id));

  const handleLinkProduct = async () => {
    if (!selectedProduct) return;
    setLinking(true);
    try {
      await projectsApi.addCustomerProduct(item.id, selectedProduct);
      setSelectedProduct("");
      await loadProductsTab();
      showToast?.("Product linked ✓");
    } catch (err) {
      showToast?.(err.message || "Failed to link product", "warn");
    } finally {
      setLinking(false);
    }
  };

  const handleRemoveProduct = async (productId) => {
    setRemovingId(productId);
    try {
      await projectsApi.removeCustomerProduct(item.id, productId);
      await loadProductsTab();
      showToast?.("Product removed ✓");
    } catch (err) {
      showToast?.(err.message || "Failed to remove product", "warn");
    } finally {
      setRemovingId(null);
    }
  };

  // ── Monitoring tab state ─────────────────────────────────────────────────────
  const [mappings,        setMappings]        = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [mappingForm,     setMappingForm]     = useState({ label_key: "", label_value: "", priority: 10 });
  const [addingMapping,   setAddingMapping]   = useState(false);
  const [removingMapping, setRemovingMapping] = useState(null);
  const [receivers,       setReceivers]       = useState([]);

  const loadMappings = useCallback(async () => {
    if (!isEdit || !isAdmin) return;
    setMappingsLoading(true);
    try {
      // Run backfill first so customer_id is set before we display alerts
      await monitoringApi.backfillMappings().catch(() => {});
      const [all, rcvList] = await Promise.all([
        monitoringApi.listTenantMappings(),
        monitoringApi.listReceivers(),
      ]);
      setMappings((Array.isArray(all) ? all : []).filter(m => m.customer_id === item.id));
      if (Array.isArray(rcvList)) setReceivers(rcvList);
    } catch {
      showToast?.("Failed to load tenant mappings", "warn");
    } finally {
      setMappingsLoading(false);
    }
  }, [isEdit, isAdmin, item?.id, showToast]);

  useEffect(() => {
    if (tab === "monitoring") loadMappings();
  }, [tab, loadMappings]);

  const handleAddMapping = async () => {
    if (!mappingForm.label_key.trim() || !mappingForm.label_value.trim()) {
      showToast?.("Label key and value are required", "warn");
      return;
    }
    setAddingMapping(true);
    try {
      await monitoringApi.createTenantMapping({
        label_key:   mappingForm.label_key.trim(),
        label_value: mappingForm.label_value.trim(),
        priority:    Number(mappingForm.priority) || 10,
        customer_id: item.id,
      });
      await monitoringApi.backfillMappings();
      setMappingForm({ label_key: "", label_value: "", priority: 10 });
      showToast?.("Mapping added ✓");
      await loadMappings();
    } catch (err) {
      showToast?.(err?.message || "Failed to add mapping", "warn");
    } finally {
      setAddingMapping(false);
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    setRemovingMapping(mappingId);
    try {
      await monitoringApi.deleteTenantMapping(mappingId);
      showToast?.("Mapping removed ✓");
      await loadMappings();
    } catch (err) {
      showToast?.(err?.message || "Failed to remove mapping", "warn");
    } finally {
      setRemovingMapping(null);
    }
  };

  // ── Details form ────────────────────────────────────────────────────────────
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.customer_name.trim()) {
      showToast?.("Customer name is required", "warn");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_name:   form.customer_name.trim(),
        customer_code:   form.customer_code || "",
        account_manager: form.account_manager || null,
      };
      if (isEdit) {
        await projectsApi.updateCustomer(item.id, { ...payload, status: form.status });
        showToast?.("Customer updated ✓");
      } else {
        await projectsApi.createCustomer(payload);
        showToast?.("Customer created ✓");
      }
      onSaved?.();
    } catch (err) {
      showToast?.(err.message || "Failed to save", "warn");
    } finally {
      setSaving(false);
    }
  };

  const custProjects = isEdit ? projects.filter(p => p.customer_id === item.id) : [];

  const TABS = [
    { key: "details",    label: "Details",  icon: "building" },
    { key: "projects",   label: "Projects", icon: "folder", badge: isEdit ? custProjects.length : 0 },
    ...(isEdit ? [{ key: "products",   label: "Products", icon: "package", badge: linkedProducts.length }] : []),
    ...(isEdit && isAdmin ? [{ key: "monitoring", label: "Monitoring", icon: "monitoring", badge: mappings.length || 0 }] : []),
  ];

  return (
    <>
      <div className="v2drw-scrim" onClick={onClose} />
      <div className="v2drw" role="dialog" aria-modal="true">
        <div className="v2drw-head">
          <div className="v2drw-head-main">
            <p className="v2drw-eyebrow">Customer</p>
            <h2 className="v2drw-title">{isEdit ? item.customer_name : "New Customer"}</h2>
          </div>
          <button className="v2drw-close" onClick={onClose}>✕</button>
        </div>

        <DrawerTabs tabs={TABS} activeTab={tab} onChange={setTab} />

        <div className="v2drw-body">
          {tab === "details" && (
            <div className="pjv2-drw-form">
              <div className="pjv2-drw-field">
                <label className="v2drw-form-label">Customer Name *</label>
                <input
                  className="v2drw-control"
                  autoFocus
                  value={form.customer_name}
                  onChange={set("customer_name")}
                />
              </div>

              <div className="pjv2-drw-field">
                <label className="v2drw-form-label">Code</label>
                <input
                  className="v2drw-control"
                  placeholder="Optional short code"
                  value={form.customer_code}
                  onChange={set("customer_code")}
                />
              </div>

              {isEdit && (
                <div className="pjv2-drw-field">
                  <label className="v2drw-form-label">Status</label>
                  <select className="v2drw-control" value={form.status} onChange={set("status")}>
                    {CUSTOMER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}

              <div className="pjv2-drw-field">
                <label className="v2drw-form-label">Account Manager</label>
                <UserSearchSelect
                  users={users}
                  value={form.account_manager || ""}
                  onChange={(id) => setForm(f => ({ ...f, account_manager: id }))}
                  placeholder="Search account manager…"
                />
              </div>
            </div>
          )}

          {tab === "projects" && (
            <div className="pjv2-proj-list">
              {custProjects.length === 0 && (
                <p className="pjv2-empty">No projects under this customer yet.</p>
              )}
              {custProjects.map(p => (
                <div key={p.id} className="pjv2-proj-item">
                  <div className="pjv2-proj-item-info">
                    <span className="pjv2-proj-item-name">{p.project_name}</span>
                    {p.project_code && (
                      <span className="pjv2-proj-item-code">{p.project_code}</span>
                    )}
                  </div>
                  <span className={`pjv2-pill ${projStatusPill(p.status)}`}>{p.status}</span>
                </div>
              ))}
            </div>
          )}

          {tab === "products" && (
            <div className="pjv2-prod-tab">
              {/* Add product row */}
              <div className="pjv2-prod-add-row">
                <div className="pjv2-prod-add-select">
                  <ProductSearchSelect
                    products={availableProducts}
                    value={selectedProduct}
                    onChange={setSelectedProduct}
                    placeholder="Search product to link…"
                  />
                </div>
                <button
                  className="pjv2-prod-add-btn"
                  onClick={handleLinkProduct}
                  disabled={!selectedProduct || linking}
                >
                  {linking ? "Linking…" : "Link"}
                </button>
              </div>

              {/* Linked products list */}
              {productsLoading && <p className="pjv2-empty">Loading…</p>}
              {!productsLoading && linkedProducts.length === 0 && (
                <p className="pjv2-empty">No products linked to this customer yet.</p>
              )}
              {!productsLoading && linkedProducts.map(p => (
                <div key={p.id} className="pjv2-prod-item">
                  <div className="pjv2-prod-item-info">
                    <span className="pjv2-prod-item-name">{p.name}</span>
                    {p.description && (
                      <span className="pjv2-prod-item-desc">{p.description}</span>
                    )}
                  </div>
                  <button
                    className="pjv2-prod-remove-btn"
                    onClick={() => handleRemoveProduct(p.id)}
                    disabled={removingId === p.id}
                    title="Remove link"
                  >
                    {removingId === p.id ? "…" : "×"}
                  </button>
                </div>
              ))}
            </div>
          )}
          {tab === "monitoring" && (
            <div className="pjv2-prod-tab">
              {/* Add mapping form */}
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
                  Map a Grafana receiver to this customer. Receivers are populated from ingested alert batches.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 80px", gap: 8, marginBottom: 8 }}>
                  <select
                    className="v2drw-control"
                    value={mappingForm.label_value}
                    onChange={e => setMappingForm(f => ({ ...f, label_key: "receiver", label_value: e.target.value }))}
                  >
                    <option value="">— Select receiver —</option>
                    {receivers.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <input
                    className="v2drw-control"
                    type="number"
                    placeholder="Priority"
                    value={mappingForm.priority}
                    onChange={e => setMappingForm(f => ({ ...f, priority: e.target.value }))}
                  />
                </div>
                <button
                  className="pjv2-prod-add-btn"
                  onClick={handleAddMapping}
                  disabled={addingMapping || !mappingForm.label_value}
                >
                  {addingMapping ? "Adding…" : "+ Add Mapping"}
                </button>
              </div>

              {/* Mappings list */}
              {mappingsLoading && <p className="pjv2-empty">Loading…</p>}
              {!mappingsLoading && mappings.length === 0 && (
                <p className="pjv2-empty">No Grafana label mappings for this customer yet.</p>
              )}
              {!mappingsLoading && mappings.map(m => (
                <div key={m.id} className="pjv2-prod-item">
                  <div className="pjv2-prod-item-info">
                    <span className="pjv2-prod-item-name">
                      <code style={{ fontFamily: "monospace", fontSize: 12, background: "#f3f4f6", padding: "1px 5px", borderRadius: 3 }}>
                        {m.label_value}
                      </code>
                    </span>
                    <span className="pjv2-prod-item-desc">Priority: {m.priority}</span>
                  </div>
                  <button
                    className="pjv2-prod-remove-btn"
                    onClick={() => handleDeleteMapping(m.id)}
                    disabled={removingMapping === m.id}
                    title="Remove mapping"
                  >
                    {removingMapping === m.id ? "…" : "×"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="v2drw-foot">
          <span className="v2drw-foot-spacer" />
          <button className="v2drw-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          {tab !== "products" && tab !== "monitoring" && (
            <button className="v2drw-btn-save" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Customer"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ── Product search combo (local — only used in this drawer) ──────────────────

function ProductSearchSelect({ products, value, onChange, placeholder }) {
  const [query, setQuery] = useState("");
  const [open,  setOpen]  = useState(false);
  const wrapRef  = useRef(null);

  const selected = products.find(p => p.id === value) || null;
  const displayValue = open ? query : (selected?.name || "");

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false); setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = (q ? products.filter(p => p.name.toLowerCase().includes(q)) : products).slice(0, 50);

  return (
    <div className={`v2drw-ss${open ? " open" : ""}`} ref={wrapRef}>
      <div className="v2drw-ss-wrap">
        <input
          type="text"
          className="v2drw-ss-input"
          value={displayValue}
          placeholder={placeholder}
          onFocus={() => { setOpen(true); setQuery(""); }}
          onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(""); }}
        />
        {selected && !open ? (
          <button className="v2drw-ss-clear" onMouseDown={e => e.preventDefault()} onClick={() => { onChange(""); setOpen(false); }} tabIndex={-1}>×</button>
        ) : (
          <svg className="v2drw-ss-chev" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </div>
      {open && (
        <div className="v2drw-ss-drop">
          <div className="v2drw-ss-list">
            {filtered.length === 0 && <div className="v2drw-ss-empty">No matches</div>}
            {filtered.map(p => (
              <div
                key={p.id}
                className={`v2drw-ss-opt${p.id === value ? " on" : ""}`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => { onChange(p.id); setOpen(false); setQuery(""); }}
              >
                {p.name}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
